/* Try not to directly import the state reactive outside the state directory.
 * State is also available through the application container.
 */

import { computed, reactive, watch } from 'vue';
import { Length } from '../Units';
import type { UserSession } from '@phi/onshape';
import type { Safe, UserWithSubscriptionStatusEntry } from '@phi/users';
import type { ID } from '@phi/newtype';
import type { Option } from '@vaaas/fpts/option';
import { KeyMap } from '../keyboard';
import { colours } from '@phi/constants';
import { is_mobile } from '@phi/util/dom';

// TODO: this should be @features/references instead, but we are awaiting a full conversion to typescript before that is possible :(
import { showing_reference_partstudio_modal } from '@features/references/reference-part-studio/state';
import { stored_viewports, showing_viewport_manager } from '@features/save-viewpoint/state';
import { showing_color_picker } from '@features/color-picker/state';
import { showing_settings_modal } from '@features/user-settings/state';
import { frontend_service_container } from '@phi/frontend/service-container';
import { showing_smoothen_options_modal } from '@features/smoothness/state';
import { showing_curvature_options_modal } from '@features/curvature-combs/state';
import { showing_read_coordinates_modal } from '@features/read-coordinates';

/**
 * - `none` — Not currently dragging.
 * - `vertex` — Dragging a vertex freely in 3D space
 * - `vertex_on_edge` — Dragging a vertex on an edge (the edge is identified in a separate variable)
 * - `edge` — Deforming an edge freely in 3D space
 * - `set` — Rigidly dragging a set of objects
 */
export type DraggingType =
    'none'
    | 'vertex'
    | 'vertex_on_edge'
    | 'edge'
    | 'set'
    | 'scale_edge_list'


const three_minutes_ago = () => {
    const t = new Date();
    t.setMinutes(t.getMinutes() - 3);
    return t;
};

/** stores the entire application's state */
const state = reactive({
    /** indicates that we should start loading the app */
    should_load_app: false,

    /** indicates that the core is initialised and the app has finished loading */
    loaded: false,

    /** indicates whether the sidebar should be shown and which bar should be visible */
    aside: undefined as undefined | 'intro' | 'tutorials' | 'morehelp',

    /** stores the current user's data in our database */
    user: undefined as undefined | Safe<UserWithSubscriptionStatusEntry>,

    /** stores the current user's onshape session */
    session: undefined as undefined | UserSession,

    /** Flag to indicate if we're running the demo version */
    is_demo: true,

    /** Flag to indicate that the user has an educational license */
    edu: false,

    /** flag to indicate that the spinner should be showing */
    spinner: undefined as undefined | string,

    display_units: Length.cm,

    tut_stage: 100,

    selected_vertices_count: 0,
    selected_edges_count: 0,
    selected_faces_count: 0,

    file_save_status: 'dirty' as 'dirty' | 'saved' | 'autosaved' | 'saving',

    /** The current working filename. Empty string signifies no selected file name. */
    current_filename: '',

    current_operation_text: '',

    rotation_lock_text: '',

    /** the application environment this application is running under
     *
     * - `undefined` — standalone, without onshape wrapper
     * - `onshape` — running inside onshape
     *
     * planned to be expanded in the future :)
     */
    framework: undefined as undefined | 'onshape',

    hidden_edges_visible: false,

    inside_red: true,

    /** number of triangles used for the mesh approximation of surfaces */
    triangles: 1600,

    /** whether the application should be listening to shortcuts. note that this basically prevents typing */
    shortcuts: true,

    /** displayed when something extraordinarily bad happens */
    catastrophic_error: undefined as undefined | string,

    /** caches the length unit used configured in onshape */
    onshape_default_length: undefined as undefined | Length,

    /** determines whether we should prevent the user from zooming */
    zoom_blocked: false,

    /** indicates that immersive mode is on */
    immersive: false,

    /** currently active modes */
    modes: [] as Array<KeyMap>,

    /** currently active keymap */
    keymap: {
        down: new Map(),
        up: new Map(),
    } as KeyMap,

    /** whether we are editing a reference partstudio body */
    editing_reference_partstudio: false,

    showing_reference_partstudio_models: false,

    showing_reference_sketches: false,

    /** whether we are editing a reference stl body */
    editing_reference_stl: false,

    /** whether rigid move manipulator is shown */
    rigid_move_manipulator: false,

    /** indicates that 3d geom objects should display their wireframe */
    wireframe_visible: false,

    /** indicates that we are currently editing reference sketches (images) */
    editing_reference_sketches: false,

    /** indicates that we are currently properly splitting invisible face connected components */
    currently_properly_splitting_invisible_face_connected_components: false,

    /** The type of dragging currently being performed */
    dragging_type: 'none' as DraggingType,

    /** whether we are panning (moving) the viewport with middle click */
    panning: false,

    consistency_issues_found: 0,

    /** whether we are rotating the viewport with right click */
    rotating: false,

    /** whether we are allowed to start a rotation of the viewport with right click */
    allow_rotate: true,

    /** Whether shift, ctrl, alt or command are pressed */
    shift_is_pressed: false,
    ctrl_is_pressed: false,
    alt_is_pressed: false,
    command_is_pressed: false,

    /** whether we are in the process of a step by ste rotation executed by clicking on the arrow keys */
    currently_rotating_viewpoint: true,

    /** whether we are drawing a line that carves geometry (knife) */
    carving: false,

    /** whether we are drawing a line that creates new edges (knife) */
    drawing_edge: false,

    in_debug_mode: false,

    canvas: {
        width: 0,
        height: 0,
    },

    /** whether the cardinal axes/planes objects are visible */
    axes_visible: false,

    /** whether shadows should be cast on a plane under our objects */
    shadows_visible: false,

    /** whether the grid on the bottom cardinal plane is visible */
    grid_visible: false,

    /** Editing edge tangent mode flag */
    editing_edge_tangent_mode: false,

    /** whether we are currently snapping geometry to another object */
    snapping_to_object: false,

    /** whether we are currently attaching to reference stl objects */
    attaching_to_stl: false,

    /** whether we are currently showing reference stl objects */
    showing_reference_stl: false,

    /** whether we are currently showing parasolid bodies */
    showing_reference_parasolid: false,

    /** whether we are currently projecting edges */
    projecting_edges: false,

    /** whether we are currently adding a vertex on an edge */
    adding_vertex: false,

    /** whether rotation is currently locked within horizontal or vertical plane */
    rotation_locked: false,

    selecting_tangent_perpendicular_plane: false,

    setting_mirror_plane: false,

    /** Flag to indicate whether a mirror plane has been selected */
    mirror_plane_selected: false,

    setting_symmetry_plane: false,

    /** Flag to indicate whether a symmetry plane has been selected */
    symmetry_plane_selected: false,

    /** Flags to indicate whether we're in one of the offsetting phases */
    offset_face_dragging: false,
    offset_face_selecting_vertex: false,

    /** Flag to indicate whether we are currently processing edges to make the symmetric, to avoid infinite loops */
    currently_making_edge_symmetric: false,

    imported_partstudio_reference_models: [] as any[],

    imported_stl_models: [] as any[],

    imported_sketches: [] as any[],

    converting_reference_to_phi: false,

    /** whether edge tangencies should be rendered */
    tangencies_are_displayed: false,

    workspace: undefined as undefined | ID<'workspace'>,

    document: undefined as undefined | ID<'document'>,

    element: undefined as undefined | ID<'element'>,

    /** currently enabled palette */
    colours: colours.light as typeof colours.light | typeof colours.dark,

    /** indicates that we are currently loading a file */
    currently_loading_model: false,

    // Remove tangencies at beginning and end if requested.
    break_terminal_tangencies: false,

    interrupt_drag_at_failed_consistency_checks: false,

    always_run_consistency_checks_on_vertices: true,

    always_run_consistency_checks_on_rest: false,

    /** in degrees */
    smoothness_threshold_for_consistency_checks: 10,

    currently_making_edges_symmetric: false,

    warning: undefined as undefined | string,

    /** parent container of onshape */
    onshape_server: 'https://cad.onshape.com',

    debug_text: undefined as Option<string>,

    currently_hiding_vertices: false,

    currently_hiding_edges: false,

    currently_hiding_faces: false,

    is_mobile: is_mobile(),

    stored_viewports,

    /** attempts to track when the user was last active */
    activity_timestamp: three_minutes_ago().getTime(),

    /** Flag to indicate we're currently in the process of exporting a model */
    currently_exporting_model: false,

    /** list of flags representing which modals are open */
    modal: {
        backgroundsModal: false,
        materialsModal: false,
        consistencyChecksModal: false,
        curvatureCombSettingsModal: false,
        DebugVarsModal: false,
        doMagicModal: false,
        drawExactCircle: false,
        ExportToOnshapeModal: false,
        ExportModal: false,
        generic_help_modal: false,
        ImportRefStlModelModal: false,
        displayTrianglesModal: false,
        okToOverwriteModal: false,
        proceedWithClearSymmetryModal: false,
        proceedWithImportModal: false,
        readCoordsModal: false,
        readSTLResizeValueModal: false,
        readValueModal: false,
        refreshModal: false,
        savePhiModelModal: false,
        scaleModal: false,
        searchModal: false,
        selectModelModal: false,
        setGridsizeModal: false,
        setModelScaleModal: false,
        projectEdgesOffset: false,
        setSmoothenStrengthModal: false,
        setVertexXYZModal: false,
        booleanOpsModal: false,
        sketchImagesModal: false,
        stillInTutorial: false,
        storeRetrieveViewportModal: false,
        vertexDistanceModal: false,
        tutorialsModalDemo: false,
        tutorialsModal: false,
        zoomSpeedModal: false,
        importTroubleshootingModal: false,
        booleanDialog: false,
    },
});

export function model_is_dirty() {
    return state.file_save_status === 'dirty'
        || state.file_save_status === 'saving';
}

export const is_connected_to_onshape = () =>
    state.framework === 'onshape'
    || frontend_service_container().authentication.session_type.value.has('onshape');

export const has_selected_document = () =>
    state.document !== undefined && state.workspace !== undefined;

export const can_save_locally_or_to_onshape = () =>
    frontend_service_container().authentication.session_type.value.has('local')
    || frontend_service_container().authentication.session_type.value.has('phi')
    || can_save_to_onshape();

export const can_save_to_onshape = () =>
    !state.is_demo
    && state.tut_stage === 100
    && is_connected_to_onshape()
    && has_selected_document();

export type State = typeof state;

export default state;

export const viewsize_to_vertex_diameter_ratio = computed(() => state.is_mobile ? 20 : 40);

export const all_modals = () => {
    const service = frontend_service_container();
    return {
        ...state.modal,
        showing_reference_partstudio_modal: showing_reference_partstudio_modal.value,
        showing_viewpoint_manager: showing_viewport_manager.value,
        showing_color_picker: showing_color_picker.value,
        showing_settings_modal: showing_settings_modal.value,
        showing_must_login_modal: frontend_service_container().authentication.showing_must_login_modal.value,
        showing_smoothen_options_modal: showing_smoothen_options_modal.value,
        showing_curvature_options_modal: showing_curvature_options_modal.value,
        showing_read_coordinates_modal: showing_read_coordinates_modal.value,
    };
};

export const hide_all_modals = () => {
    for (const k of Object.keys(state.modal))
        state.modal[k as keyof State['modal']] = false;

    showing_reference_partstudio_modal.value;
    showing_viewport_manager.value;
    showing_color_picker.value;
    showing_settings_modal.value;
    showing_smoothen_options_modal.value;
    showing_curvature_options_modal.value;
    showing_read_coordinates_modal.value;

    const service = frontend_service_container();
    service.authentication.showing_must_login_modal.value;
};

export const count_of_open_modals = (): number => Object.values(all_modals()).filter(Boolean).length;

/** deselect the current onshape document, returning back to a state where no document is selected
 *
 * this **only** happens in standalone mode. as an onshape iframe, this is a no-op.
 */
export const deselect_onshape_document = () => {
    if (state.framework === undefined) {
        state.document = undefined;
        state.workspace = undefined;
    }
};

export const in_tutorial = (): boolean => state.tut_stage < 100;

watch(
    () => state.interrupt_drag_at_failed_consistency_checks,
    (value) => {
        if (value)
            state.always_run_consistency_checks_on_rest = true;
    }
);

watch(
    () => [state.always_run_consistency_checks_on_rest, state.always_run_consistency_checks_on_vertices] as const,
    ([a, b]) => {
        if (!a && !b)
            state.interrupt_drag_at_failed_consistency_checks = false;
    }
);
